import { getAccessToken, renewAccessToken } from '@/lib/auth/tokenStore';
import { env } from '@/lib/env';

/**
 * The one place a request to the API is made, and therefore the one place the
 * access token is attached and a lapsed session is renewed.
 *
 * Renewal lives *inside* this function rather than in an interceptor because this
 * seam resolves for 4xx and 5xx — a 401 is a value here, not a rejected promise,
 * so there is no error path to hang retry logic on. On a 401 it renews once,
 * through the single-flight guard in the token store, and replays the original
 * request exactly once. A second 401 after a successful renewal means something
 * other than expiry, and retrying further would loop.
 *
 * A 401 is only a sign of expiry when it answers the token the store holds now.
 * Just after sign-in, requests fired before the token arrived come back 401 with
 * the fresh token already in place; renewing then swapped the user the sign-in
 * callback was reading, and it sent the person to the home page instead of the
 * page they had opened. Such a request is sent again with the token it lacked.
 */
async function send(
  fullUrl: string,
  options: RequestInit,
  headers: Record<string, string>,
  token: string | null,
) {
  return fetch(fullUrl, {
    ...options,
    headers: token ? { ...headers, Authorization: `Bearer ${token}` } : headers,
  });
}

export async function customFetch<T>(
  url: string,
  options?: RequestInit,
): Promise<T> {
  // Generated operation URLs already include the `/api` prefix (the backend
  // mounts public routers under /api and the OpenAPI records full paths).
  const fullUrl = `${env.VITE_API_BASE_URL}${url}`;

  // A FormData body must not get an explicit Content-Type: fetch derives
  // `multipart/form-data` with the part boundary itself, and a hardcoded value
  // would produce an unparseable request (the import operation uploads a file).
  const contentType: Record<string, string> =
    options?.body instanceof FormData
      ? {}
      : { 'Content-Type': 'application/json' };

  const baseHeaders: Record<string, string> = {
    ...contentType,
    Accept: 'application/json',
    ...(options?.headers as Record<string, string> | undefined),
  };

  const sentWith = getAccessToken();
  let response = await send(fullUrl, options ?? {}, baseHeaders, sentWith);

  if (response.status === 401) {
    const current = getAccessToken();
    if (current !== null && current !== sentWith) {
      // The token arrived, or was renewed, while this request was out.
      response = await send(fullUrl, options ?? {}, baseHeaders, current);
    } else if (current !== null) {
      const renewed = await renewAccessToken();
      if (renewed) {
        response = await send(fullUrl, options ?? {}, baseHeaders, renewed);
      }
    }
  }

  const { headers, status } = response;

  let data: unknown;
  if (status === 204 || response.headers.get('content-length') === '0') {
    data = undefined;
  } else {
    data = await response.json().catch(() => undefined);
  }

  return { data, status, headers } as T;
}

/**
 * Narrows a generated response envelope to one status and returns its body.
 *
 * `customFetch` resolves for every response, including 4xx and 5xx — a failed
 * request is not a rejected promise. Callers therefore have to say which status
 * they mean, and the generated response types are discriminated unions keyed on
 * `status`, so checking it is also what types `data` correctly. This does both
 * in one place: the single cast below is what the `status` check has already
 * proven, and it lives here rather than being repeated at every call site.
 *
 * Throwing on the unexpected status is deliberate — React Query turns a thrown
 * error into `isError`, which is how a caller tells "this failed" from "this
 * succeeded and was empty".
 */
export function expectStatus<
  R extends { status: number; data: unknown },
  S extends R['status'],
>(response: R, status: S, message: string): Extract<R, { status: S }>['data'] {
  if (response.status !== status) {
    throw new Error(message);
  }
  return (response as Extract<R, { status: S }>).data;
}
